'use client'
import { createContext, ReactNode, useEffect, useState } from 'react'

import i18n from 'web/lib/i18n'
import { identifyUser, setUserProperty } from 'web/lib/service/analytics'
import { useStateCheckEquality } from 'web/hooks/use-state-check-equality'
import { BANNED_TRADING_USER_IDS } from 'common/envs/constants'
import { type PrivateUser, type User, type UserAndPrivateUser } from 'common/user'
import { nativePassUsers, nativeSignOut } from 'web/lib/native/native-messages'
import { safeLocalStorage } from 'web/lib/util/local'
import { getSavedContractVisitsLocally } from 'web/hooks/use-save-visits'
import { useWebsocketUser, useWebsocketPrivateUser } from 'web/hooks/use-user'
import { useEffectCheckEquality } from 'web/hooks/use-effect-check-equality'
import { getPrivateUserSafe, getUserSafe } from 'web/lib/supabase/users'
import toast from 'react-hot-toast'
import { Row } from './layout/row'
import { TokenNumber } from './widgets/token-number'
import { getSupabaseBrowserClient } from 'web/lib/supabase/client'
import { setTokenProvider } from 'common/util/api'
import { createUser } from 'web/lib/api/api'
import { randomString } from 'common/util/random'
import { ensureDeviceToken } from 'web/lib/util/device-token'
import { logout as supabaseLogout } from 'web/lib/supabase/auth'

// Either we haven't looked up the logged in user yet (undefined), or we know
// the user is not logged in (null), or we know the user is logged in.
export type AuthUser =
  | undefined
  | null
  | (UserAndPrivateUser & { authLoaded: boolean })
const CACHED_USER_KEY = 'CACHED_USER_KEY_V2'

export const AuthContext = createContext<AuthUser>(undefined)

export function AuthProvider(props: {
  children: ReactNode
  serverUser?: AuthUser
}) {
  const { children, serverUser } = props

  const [user, setUser] = useStateCheckEquality<User | undefined | null>(
    serverUser ? serverUser.user : serverUser
  )
  const [privateUser, setPrivateUser] = useStateCheckEquality<
    PrivateUser | undefined
  >(serverUser ? serverUser.privateUser : undefined)
  const [authLoaded, setAuthLoaded] = useState(false)

  const authUser = !user
    ? user
    : !privateUser
    ? privateUser
    : { user, privateUser, authLoaded }

  // Initialize Supabase token provider for API calls
  useEffect(() => {
    const supabase = getSupabaseBrowserClient()
    setTokenProvider(async () => {
      const { data: { session } } = await supabase.auth.getSession()
      return session?.access_token ?? null
    })
  }, [])

  // Restore cached user on mount
  useEffect(() => {
    if (serverUser === undefined) {
      const cachedUser = safeLocalStorage?.getItem(CACHED_USER_KEY)
      const parsed = cachedUser ? JSON.parse(cachedUser) : undefined
      if (parsed) {
        setUser(parsed.user)
        setPrivateUser(parsed.privateUser)
        setAuthLoaded(false)
      } else setUser(undefined)
    }
  }, [serverUser])

  // Persist/clear cache when auth state changes
  useEffect(() => {
    if (authUser) {
      if (
        BANNED_TRADING_USER_IDS.includes(authUser.user.id) ||
        authUser.user.userDeleted
      ) {
        const message = authUser.user.userDeleted
          ? i18n.t('auth.deletedAccountMessage')
          : i18n.t('auth.bannedMessage')

        supabaseLogout().then(() => {
          alert(message)
        })
        return
      }
      safeLocalStorage?.setItem(CACHED_USER_KEY, JSON.stringify(authUser))
    } else if (authUser === null) {
      safeLocalStorage?.removeItem(CACHED_USER_KEY)
    }
  }, [authUser])

  const onAuthLoad = (
    supabaseUserId: string,
    user: User,
    privateUser: PrivateUser
  ) => {
    setUser(user)
    setPrivateUser(privateUser)
    setAuthLoaded(true)

    nativePassUsers(
      JSON.stringify({
        supabaseUserId,
        privateUser,
      })
    )
  }

  // ─── Supabase auth state listener ────────────────────────────────────────
  useEffect(() => {
    const supabase = getSupabaseBrowserClient()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event: string, session: any) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
          if (session?.user) {
            const supabaseUserId = session.user.id

            try {
              const [appUser, appPrivateUser] = await Promise.all([
                getUserSafe(supabaseUserId),
                getPrivateUserSafe(),
              ])

              if (!appUser || !appPrivateUser) {
                const deviceToken = ensureDeviceToken()
                const newUser = (await createUser({
                  deviceToken,
                  adminToken: '',
                  visitedContractIds: getSavedContractVisitsLocally(),
                })) as UserAndPrivateUser

                onAuthLoad(supabaseUserId, newUser.user, newUser.privateUser)
              } else {
                onAuthLoad(supabaseUserId, appUser, appPrivateUser)
              }
            } catch (e) {
              console.error('Auth: failed to load or create user', e)
              safeLocalStorage?.removeItem(CACHED_USER_KEY)
              setUser(null)
              setPrivateUser(undefined)
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null)
          setPrivateUser(undefined)
          nativeSignOut()
          if (safeLocalStorage?.getItem(CACHED_USER_KEY)) localStorage.clear()
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const uid = authUser ? authUser.user.id : authUser

  useEffect(() => {
    if (uid) {
      identifyUser(uid)
    } else if (uid === null) {
      identifyUser(null)
    }
  }, [uid])

  const listenUser = useWebsocketUser(uid ?? undefined)
  useEffectCheckEquality(() => {
    if (authLoaded && listenUser) {
      if (user) {
        const balanceChange = listenUser.balance - user.balance
        const cashBalanceChange = listenUser.cashBalance - user.cashBalance

        if (balanceChange > 0 || cashBalanceChange > 0) {
          showToast(balanceChange, cashBalanceChange)
        }
      }
      setUser(listenUser)
    }
  }, [authLoaded, listenUser])

  const listenPrivateUser = useWebsocketPrivateUser(uid ?? undefined)
  useEffectCheckEquality(() => {
    if (authLoaded && listenPrivateUser) setPrivateUser(listenPrivateUser)
  }, [authLoaded, listenPrivateUser])

  const username = authUser?.user.username
  useEffect(() => {
    if (username != null) {
      setUserProperty('username', username)
    }
  }, [username])

  return (
    <AuthContext.Provider value={authUser}>{children}</AuthContext.Provider>
  )
}

const showToast = (manaChange: number, cashChange: number) => {
  toast.success(
    <Row className="gap-1">
      <span>{i18n.t('toasts.chaChing')}</span>
      {manaChange > 0 && (
        <Row className="items-center justify-center">
          +
          <TokenNumber
            amount={manaChange}
            className="font-bold"
            coinType="MANA"
          />
          {cashChange > 0 && <span className="mx-1">&</span>}
        </Row>
      )}
      {cashChange > 0 && (
        <Row className="items-center justify-center">
          +
          <TokenNumber
            amount={cashChange}
            className="font-bold"
            coinType="CASH"
          />
        </Row>
      )}
    </Row>,
    { duration: 5000, icon: '🎉' }
  )
}
